import { toast } from "react-toastify";
import { ACTION_TYPES } from "./types";
import dayjs from "dayjs";
import { redirectToAuth } from "../helpers";
import axiosInstance from "../../services/api";

const BASE_URL = "https://cx.api.averly.com.na";

// Get export count
export const getExportCount = (data) => async (dispatch) => {
  const { organizationId, branchId, createdFrom, createdTo } = data;

  if (!organizationId || !createdFrom || !createdTo) {
    toast.error("Please select organization and month.");
    return;
  }

  dispatch({ type: ACTION_TYPES.UPDATE_IS_FETCHING_EXPORT, payload: true });

  try {
    const endpoint = `${BASE_URL}/management/tickets/export/count`;
    const res = await axiosInstance.get(endpoint, {
      params: {
        organizationId,
        branchId: branchId || "",
        createdFrom,
        createdTo,
      },
    });

    const count = res.data.count;
    dispatch({ type: ACTION_TYPES.EXPORT_COUNT, payload: count });

    if (count === 0) {
      toast.info("No tickets found for the selected period.");
    } else {
      toast.success(`Found ${count.toLocaleString()} tickets ready for export.`);
    }
  } catch (err) {
    console.error("Error fetching export count:", err);

    // Don't report a failed request as "0 tickets" - surface the error instead
    let message = "Could not count tickets. Please try again.";
    if (err.response?.status === 401) {
      message = "Session expired. Please log in again.";
    } else if (err.response?.status === 504 || err.code === "ECONNABORTED") {
      message = "The server took too long to count tickets. Please try again.";
    } else if (err.response) {
      message = "Server error while counting tickets. Please try again later.";
    } else if (err.request) {
      message = "Network error. Please check your connection.";
    }

    dispatch({ type: ACTION_TYPES.EXPORT_COUNT_ERROR, payload: message });
    redirectToAuth(err, message);
  }

  dispatch({ type: ACTION_TYPES.UPDATE_IS_FETCHING_EXPORT, payload: false });
};

// Set time unit
export const setExportTimeUnit = (timeUnit) => (dispatch) => {
  dispatch({ type: ACTION_TYPES.SET_EXPORT_TIME_UNIT, payload: timeUnit });
};

// Download export CSV
export const downloadExport = (data) => async (dispatch) => {
  const { organizationId, branchId, createdFrom, createdTo, timeUnit = "seconds" } = data;

  if (!organizationId || !createdFrom || !createdTo) {
    toast.error("Please select organization and month.");
    return;
  }

  dispatch({ type: ACTION_TYPES.UPDATE_IS_DOWNLOADING_EXPORT, payload: true });

  // Show loading toast with ID so we can dismiss it later
  const loadingToastId = toast.loading("Starting download...", {
    autoClose: false,
  });

  try {
    // Update toast to show progress
    toast.update(loadingToastId, {
      render: "Preparing export data...",
    });

    const endpoint = `${BASE_URL}/management/tickets/export/download`;
    const res = await axiosInstance.get(endpoint, {
      params: {
        organizationId,
        branchId: branchId || "",
        createdFrom,
        createdTo,
        timeUnit,
      },
      responseType: "blob",
    });

    // Update toast
    toast.update(loadingToastId, {
      render: "Download starting...",
    });

    // Create blob and trigger download
    const blob = new Blob([res.data], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);

    // Generate filename with month
    const monthStr = dayjs.unix(createdFrom).format("YYYY-MM");
    link.download = `tickets_export_${monthStr}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL
    URL.revokeObjectURL(link.href);

    // Dismiss loading toast and show success
    toast.dismiss(loadingToastId);
    toast.success("Export downloaded successfully!");
  } catch (err) {
    console.error("Error downloading export:", err);

    // Dismiss loading toast and show error
    toast.dismiss(loadingToastId);

    // Check for specific error types
    if (err.response) {
      if (err.response.status === 401) {
        toast.error("Session expired. Please log in again.");
        redirectToAuth(err, "Session expired");
      } else if (err.response.status === 504) {
        toast.error("The server took too long to prepare the export. Please try again.");
      } else if (err.response.status === 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Download failed. Please try again.");
      }
    } else if (err.request) {
      toast.error("Network error. Please check your connection.");
    } else {
      toast.error("Download failed. Please try again.");
    }
  }

  dispatch({ type: ACTION_TYPES.UPDATE_IS_DOWNLOADING_EXPORT, payload: false });
};

// Reset export state
export const resetExportState = () => (dispatch) => {
  dispatch({ type: ACTION_TYPES.RESET_EXPORT_STATE });
};
